// English and German MUI locales are statically imported (always available)

import type { Localization } from '@mui/material/locale';
import { deDE, enUS } from '@mui/material/locale';
import type { ThemeOptions } from '@mui/material/styles';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { deDE as deDEGrid, enUS as enUSGrid } from '@mui/x-data-grid-pro/locales';
import deepmerge from 'deepmerge';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router';
import { Link as RouterLink } from 'react-router';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { defaultTheme } from '~/shared/customization/customization-response';
import type { LanguageConfig } from '~/shared/internationalization';

import { useColorScheme } from '../color-scheme/ColorSchemeProvider';
import { readSemanticColor } from '../color-scheme/color-scheme';

const LinkBehavior = forwardRef<
    HTMLAnchorElement,
    Omit<RouterLinkProps, 'to'> & { readonly href: RouterLinkProps['to'] }
>((props, ref) => {
    const { href, ...other } = props;

    return <RouterLink ref={ref} to={href} {...other} />;
});

export type MuiLocaleBundle = {
    readonly gridLocale: typeof enUSGrid;
    readonly locale: Localization;
};

// English bundle is always available (fallback)
const englishMuiBundle: MuiLocaleBundle = {
    gridLocale: enUSGrid,
    locale: enUS,
};

// German bundle is always available
const germanMuiBundle: MuiLocaleBundle = {
    gridLocale: deDEGrid,
    locale: deDE,
};

/**
 * Loads MUI locale bundle dynamically.
 * English and German are returned immediately (statically imported).
 * Other languages are loaded at runtime with a fallback to English on failure.
 */
export const loadMuiLocaleBundle = async (code: LanguageConfig['code']): Promise<MuiLocaleBundle> => {
    switch (code) {
        case 'en-US':
            return englishMuiBundle;
        case 'de-DE':
            return germanMuiBundle;
        default:
            // Dynamically load other languages
            break;
    }

    try {
        switch (code) {
            case 'es-ES': {
                const [{ esES }, { esES: esESGrid }] = await Promise.all([
                    import('@mui/material/locale'),
                    import('@mui/x-data-grid-pro/locales'),
                ]);

                return { gridLocale: esESGrid, locale: esES };
            }
            case 'fr-FR': {
                const [{ frFR }, { frFR: frFRGrid }] = await Promise.all([
                    import('@mui/material/locale'),
                    import('@mui/x-data-grid-pro/locales'),
                ]);

                return { gridLocale: frFRGrid, locale: frFR };
            }
            case 'ja-JP': {
                const [{ jaJP }, { jaJP: jaJPGrid }] = await Promise.all([
                    import('@mui/material/locale'),
                    import('@mui/x-data-grid-pro/locales'),
                ]);

                return { gridLocale: jaJPGrid, locale: jaJP };
            }
            default:
                return englishMuiBundle;
        }
    } catch {
        // Failed to load MUI locale, falling back to English

        return englishMuiBundle;
    }
};

const getInitialMuiBundle = (code: LanguageConfig['code']): MuiLocaleBundle => {
    switch (code) {
        case 'de-DE':
            return germanMuiBundle;
        // biome-ignore lint/complexity/noUselessSwitchCase: suppressed due to migration
        case 'en-US':
        default:
            return englishMuiBundle;
    }
};

const createDarkMuiThemeOptions = (): ThemeOptions => {
    const surfaceCanvas = readSemanticColor('--semantic-surface-canvas', '#040f16');
    const surfaceRaised = readSemanticColor('--semantic-surface-raised', '#131d24');
    const surfaceInteractive = readSemanticColor('--semantic-surface-interactive', '#1d272d');
    const actionSecondaryHover = readSemanticColor('--semantic-action-secondary-hover', '#293339');
    const actionDisabled = readSemanticColor('--semantic-action-disabled', '#252f35');
    const borderMuted = readSemanticColor('--semantic-border-muted', '#252f35');
    const borderStrong = readSemanticColor('--semantic-border-strong', '#353e45');
    const textPrimary = readSemanticColor('--semantic-text-primary', '#e2e3e8');
    const textSecondary = readSemanticColor('--semantic-text-secondary', '#caccd0');
    const textMuted = readSemanticColor('--semantic-text-muted', '#989da2');
    const textDisabled = readSemanticColor('--semantic-text-disabled', '#676d73');
    const jobPosting = readSemanticColor('--semantic-entity-job-content', '#ff709f');
    const learningOpportunity = readSemanticColor('--semantic-entity-learning-content', '#a993ff');
    const user = readSemanticColor('--semantic-entity-user-content', '#64d2e8');

    return {
        components: {
            MuiDivider: {
                styleOverrides: {
                    root: {
                        borderBottomColor: borderMuted,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    elevation1: {
                        boxShadow: 'var(--shadow-md)',
                    },
                },
            },
        },
        palette: {
            action: {
                active: textSecondary,
                disabled: textDisabled,
                disabledBackground: actionDisabled,
                focus: actionSecondaryHover,
                hover: surfaceInteractive,
                selected: actionSecondaryHover,
            },
            background: {
                default: surfaceCanvas,
                paper: surfaceRaised,
            },
            cardborder: {
                main: borderStrong,
            },
            divider: borderMuted,
            jobposting: {
                main: jobPosting,
            },
            learningOpportunity: {
                main: learningOpportunity,
            },
            mode: 'dark',
            secondary: {
                contrastText: surfaceCanvas,
                dark: textMuted,
                light: textPrimary,
                main: textSecondary,
            },
            text: {
                disabled: textDisabled,
                primary: textPrimary,
                secondary: textSecondary,
            },
            user: {
                main: user,
            },
        },
    };
};

const lightMuiThemeOptions: ThemeOptions = {
    palette: {
        mode: 'light',
    },
};

export const CustomThemeProvider: FunctionComponent<PropsWithChildren> = (props) => {
    const { applicationState } = useApplicationContext();
    const { colorScheme } = useColorScheme();
    const languageCode = applicationState.language.code;

    const [muiBundle, setMuiBundle] = useState<MuiLocaleBundle>(() => getInitialMuiBundle(languageCode));

    useEffect(() => {
        let cancelled = false;

        if (languageCode === 'en-US' || languageCode === 'de-DE') {
            setMuiBundle(getInitialMuiBundle(languageCode));
        } else {
            loadMuiLocaleBundle(languageCode).then((bundle) => {
                if (!cancelled) {
                    setMuiBundle(bundle);
                }
            });
        }

        return () => {
            cancelled = true;
        };
    }, [languageCode]);

    const uiConfigurationTheme = applicationState.customization
        .get('uiConfiguration')
        .get('theme')
        .unwrapOr(defaultTheme) as ThemeOptions;

    const colorSchemeThemeOptions = colorScheme === 'dark' ? createDarkMuiThemeOptions() : lightMuiThemeOptions;
    const baseTheme = colorScheme === 'light' ? uiConfigurationTheme : (defaultTheme as ThemeOptions);
    const theme = createTheme(
        deepmerge(deepmerge(baseTheme, colorSchemeThemeOptions), {
            components: {
                MuiButtonBase: { defaultProps: { LinkComponent: LinkBehavior } },
                MuiChip: {
                    styleOverrides: {
                        root: {
                            borderRadius: '0.5rem',
                        },
                    },
                },
                MuiLink: { defaultProps: { component: LinkBehavior } },
            },
        }),
        muiBundle.locale,
        muiBundle.gridLocale,
    );

    return <ThemeProvider theme={theme}>{props.children}</ThemeProvider>;
};

export const Shade = {
    p12: 0.12,
    p30: 0.3,
    p4: 0.04,
    p60: 0.6,
    p8: 0.08,
} as const;
